import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["type", "name", "id", "placeholder", "label", "errorText", "required", "readonly", "error", "showValidState", "value", "onChange", "onBlur", "disabled", "resetable", "icon", "rightSecondIcon", "clearIcon", "onClear", "dataHook", "onAutoFill", "autoComplete", "helperLink", "helperText", "tokens", "after", "isLoading", "fullErrorMessageGap"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, forwardRef, useRef, useImperativeHandle, useState } from 'react';
import { transparentize } from 'polished';
import { AnimatePresence } from 'framer-motion';
import { ButtonLoader } from '@segment/matcha/components/Button';
import { ClearIcon, ErrorIcon, CheckIcon } from '@segment/matcha/components/Icons/Common';
import { useFieldset } from '@segment/matcha/components/Fieldset/FieldsetContext';
import { withDesignTokens } from '@segment/matcha/useDesignTokens';
import * as views from './Input.views';
import ErrorSlot from './ErrorSlot';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment as _Fragment } from "@emotion/react/jsx-runtime";
var BaseInput = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _ref$type = _ref.type,
      type = _ref$type === void 0 ? "text" : _ref$type,
      name = _ref.name,
      id = _ref.id,
      placeholder = _ref.placeholder,
      label = _ref.label,
      errorText = _ref.errorText,
      required = _ref.required,
      readonly = _ref.readonly,
      error = _ref.error,
      showValidState = _ref.showValidState,
      _ref$value = _ref.value,
      externalValue = _ref$value === void 0 ? "" : _ref$value,
      _onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      disabled = _ref.disabled,
      resetable = _ref.resetable,
      icon = _ref.icon,
      rightSecondIcon = _ref.rightSecondIcon,
      _ref$clearIcon = _ref.clearIcon,
      clearIcon = _ref$clearIcon === void 0 ? _jsx(ClearIcon, {}) : _ref$clearIcon,
      onClear = _ref.onClear,
      dataHook = _ref.dataHook,
      onAutoFill = _ref.onAutoFill,
      _ref$autoComplete = _ref.autoComplete,
      autoComplete = _ref$autoComplete === void 0 ? "on" : _ref$autoComplete,
      helperLink = _ref.helperLink,
      helperText = _ref.helperText,
      tokens = _ref.tokens,
      after = _ref.after,
      _ref$isLoading = _ref.isLoading,
      isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
      fullErrorMessageGap = _ref.fullErrorMessageGap,
      rest = _objectWithoutProperties(_ref, _excluded);

  var fieldRef = useRef(null);
  var fieldsetContext = useFieldset();
  var isDisabled = disabled || (fieldsetContext === null || fieldsetContext === void 0 ? void 0 : fieldsetContext.disabledFieldset);

  var _useState = useState(externalValue),
      value = _useState[0],
      setValue = _useState[1];

  useEffect(function () {
    setValue(externalValue);
  }, [externalValue]);
  useImperativeHandle(ref, function () {
    return fieldRef.current;
  });
  return _jsxs(views.Wrapper, _objectSpread(_objectSpread({}, tokens.wrapper), {}, {
    children: [label && _jsx(views.Label, _objectSpread(_objectSpread({
      htmlFor: id,
      variant: "formLabel",
      tag: "label"
    }, tokens.label), {}, {
      children: label
    })), _jsxs(views.FieldWrapper, {
      children: [icon && _jsx(views.LeftIconSlot, {
        color: tokens.field.color,
        children: icon
      }), _jsx(views.Field, _objectSpread(_objectSpread({
        type: type,
        name: name,
        id: id,
        placeholder: placeholder,
        required: required,
        disabled: isDisabled,
        readOnly: readonly,
        value: value,
        onChange: function onChange(event) {
          if (!readonly) {
            setValue(event.target.value);
            _onChange === null || _onChange === void 0 ? void 0 : _onChange(event, event.target.value);

            if (event.target.value.length > value.length + 2 && event.target.value !== value && event.target.value !== "") {
              onAutoFill === null || onAutoFill === void 0 ? void 0 : onAutoFill(event.target.value);
            }
          }
        },
        ref: fieldRef,
        "aria-invalid": error,
        "aria-errormessage": "".concat(name, "-message"),
        onBlur: onBlur,
        "data-hook": dataHook,
        autoComplete: autoComplete
      }, rest), tokens.field)), _jsxs(views.RightIconsWrapper, {
        children: [error && !isLoading && _jsx(views.RightIconSlot, {
          children: _jsx(ErrorIcon, _objectSpread({}, tokens.errorIcon))
        }), showValidState && !error && value && !isLoading && _jsx(views.RightIconSlot, {
          children: _jsx(CheckIcon, _objectSpread({}, tokens.successIcon))
        }), isLoading && _jsx(views.RightIconSlot, {
          children: _jsx(ButtonLoader, {
            loading: isLoading,
            delay: 0
          })
        }), resetable && Boolean(value) && _jsx(views.RightIconSlot, {
          color: tokens.field.color,
          children: _jsx(views.ClearButton, {
            "aria-label": "Reset value",
            onClick: function onClick() {
              setValue("");
              onClear === null || onClear === void 0 ? void 0 : onClear();
            },
            children: clearIcon
          })
        }), rightSecondIcon && _jsx(views.RightIconSlot, {
          color: tokens.field.color,
          disableEvents: true,
          children: rightSecondIcon
        })]
      })]
    }), after ? _jsxs(_Fragment, {
      children: [after, _jsx(AnimatePresence, {
        children: errorText && fullErrorMessageGap ? _jsx(views.AnimatedErrorMessageGap, {
          initial: {
            height: 0
          },
          animate: {
            height: fullErrorMessageGap
          },
          exit: {
            height: 0
          }
        }) : null
      })]
    }) : null, errorText ? _jsx(ErrorSlot, {
      inputName: name,
      errorText: errorText,
      tokens: tokens.errorLabel,
      helperLink: helperLink,
      helperText: helperText,
      fullErrorMessageGap: fullErrorMessageGap
    }) : null]
  }));
});

var getFieldTokens = function getFieldTokens(props) {
  var basePaddingRight = 20;
  var hasRightIcon = Boolean(props.error || props.showValidState || props.resetable);
  var hasSecondRightIcon = Boolean(props.rightSecondIcon);
  var extraPaddingRight = [hasRightIcon, hasSecondRightIcon].reduce(function (totalExtraPadding, hasIcon) {
    return hasIcon ? totalExtraPadding + 24 : totalExtraPadding;
  }, 0);
  return {
    paddingRight: "".concat(basePaddingRight + extraPaddingRight, "px"),
    paddingLeft: props.icon ? "55px" : ""
  };
};

export var Input = withDesignTokens(BaseInput, function (props) {
  return {
    wrapper: _objectSpread(_objectSpread(_objectSpread({}, props.disabled && {
      opacity: 0.75
    }), props.expanded && {
      width: "100%"
    }), {}, {
      hidden: props.type === "hidden"
    })
  };
}, {
  light: function light(theme, props) {
    return {
      field: _objectSpread(_objectSpread({}, getFieldTokens(props)), {}, {
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.brand.darkAcai[300],
        color: theme.palette.brand.darkAcai[700],
        shadowColor: theme.palette.brand.darkAcai[300],
        placeholderColor: theme.palette.brand.darkAcai[500],
        placeholderOpacity: 0.75,
        hoverBackgroundColor: theme.palette.brand.darkAcai[100],
        hoverBorderColor: theme.palette.brand.darkAcai[300],
        hoverPlaceholderColor: theme.palette.brand.darkAcai[500],
        focusBorderColor: theme.palette.brand.darkAcai[600],
        focusShadowColor: theme.palette.brand.darkAcai[600],
        disabledBorderColor: theme.palette.brand.darkAcai[300],
        disabledOpacity: 1,
        autoFillColor: theme.palette.brand.darkAcai[700],
        autoFillCaretColor: theme.palette.brand.darkAcai[700]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      }),
      label: {
        color: theme.palette.brand.darkAcai[500]
      },
      successIcon: {
        color: theme.palette.status.success[200]
      },
      errorIcon: {
        color: theme.palette.status.error[200]
      }
    };
  },
  dark: function dark(theme, props) {
    return {
      field: _objectSpread(_objectSpread({}, getFieldTokens(props)), {}, {
        backgroundColor: theme.palette.brand.darkAcai[600],
        borderColor: theme.palette.brand.darkAcai[600],
        color: theme.palette.neutrals.white[100],
        shadowColor: theme.palette.brand.darkAcai[600],
        placeholderColor: theme.palette.brand.darkAcai[200],
        placeholderOpacity: 0.7,
        hoverBackgroundColor: theme.palette.brand.darkAcai[600],
        hoverBorderColor: transparentize(0.9, theme.palette.neutrals.white[100]),
        hoverShadowColor: transparentize(0.9, theme.palette.neutrals.white[100]),
        hoverPlaceholderColor: theme.palette.brand.darkAcai[200],
        focusBorderColor: theme.palette.brand.darkAcai[300],
        focusShadowColor: theme.palette.brand.darkAcai[300],
        disabledBorderColor: theme.palette.brand.darkAcai[600],
        disabledOpacity: 0.7,
        autoFillColor: theme.palette.neutrals.white[100],
        autoFillCaretColor: theme.palette.neutrals.white[100]
      }, props.error && {
        borderColor: theme.palette.status.error[100],
        shadowColor: theme.palette.status.error[100],
        hoverBorderColor: theme.palette.status.error[100],
        hoverShadowColor: theme.palette.status.error[100],
        focusBorderColor: theme.palette.status.error[100],
        focusShadowColor: theme.palette.status.error[100]
      }),
      label: {
        color: theme.palette.neutrals.white[100]
      },
      successIcon: {
        color: theme.palette.status.success[100]
      },
      errorIcon: {
        color: theme.palette.status.error[100]
      }
    };
  },
  twilioLight: function twilioLight(theme, props) {
    return {
      field: _objectSpread(_objectSpread({}, getFieldTokens(props)), {}, {
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.twilio.gray[30],
        color: theme.palette.twilio.gray[100],
        placeholderColor: theme.palette.twilio.gray[60],
        hoverBackgroundColor: theme.palette.neutrals.white[100],
        hoverBorderColor: theme.palette.twilio.gray[60],
        hoverPlaceholderColor: theme.palette.twilio.gray[80],
        focusBorderColor: theme.palette.twilio.gray[60],
        focusShadowColor: theme.palette.twilio.gray[60],
        disabledBorderColor: theme.palette.twilio.gray[30],
        disabledOpacity: 0.5,
        autoFillColor: theme.palette.brand.darkAcai[700],
        autoFillCaretColor: theme.palette.brand.darkAcai[700]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      }),
      label: {
        color: theme.palette.twilio.gray[80]
      },
      successIcon: {
        color: theme.palette.twilio.blue[60]
      },
      errorIcon: {
        color: theme.palette.status.error[200]
      }
    };
  },
  twilioDark: function twilioDark(theme, props) {
    return {
      field: _objectSpread(_objectSpread({}, getFieldTokens(props)), {}, {
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.twilio.gray[30],
        color: theme.palette.twilio.gray[100],
        placeholderColor: theme.palette.twilio.gray[60],
        hoverBackgroundColor: theme.palette.neutrals.white[100],
        hoverBorderColor: theme.palette.twilio.gray[60],
        hoverPlaceholderColor: theme.palette.twilio.gray[80],
        focusBorderColor: theme.palette.twilio.gray[60],
        focusShadowColor: theme.palette.twilio.gray[60],
        disabledBorderColor: theme.palette.twilio.gray[30],
        disabledOpacity: 0.5,
        autoFillColor: theme.palette.brand.darkAcai[700],
        autoFillCaretColor: theme.palette.brand.darkAcai[700]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      }),
      label: {
        color: theme.palette.neutrals.white[100]
      },
      successIcon: {
        color: theme.palette.twilio.blue[60]
      },
      errorIcon: {
        color: theme.palette.status.error[200]
      }
    };
  },
  cdpWeek2023: function cdpWeek2023(theme, props) {
    return {
      field: _objectSpread(_objectSpread({}, getFieldTokens(props)), {}, {
        backgroundColor: theme.palette.neutrals.white[100],
        borderColor: theme.palette.twilio.gray[30],
        color: theme.palette.twilio.gray[100],
        placeholderColor: theme.palette.twilio.gray[60],
        hoverBackgroundColor: theme.palette.neutrals.white[100],
        hoverBorderColor: theme.palette.twilio.gray[60],
        hoverPlaceholderColor: theme.palette.twilio.gray[80],
        focusBorderColor: theme.palette.twilio.gray[60],
        focusShadowColor: theme.palette.twilio.gray[60],
        disabledBorderColor: theme.palette.twilio.gray[30],
        disabledOpacity: 0.5,
        autoFillColor: theme.palette.brand.darkAcai[700],
        autoFillCaretColor: theme.palette.brand.darkAcai[700]
      }, props.error && {
        borderColor: theme.palette.status.error[200],
        shadowColor: theme.palette.status.error[200],
        hoverBorderColor: theme.palette.status.error[200],
        focusBorderColor: theme.palette.status.error[200],
        focusShadowColor: theme.palette.status.error[200]
      }),
      label: {
        color: theme.palette.twilio.gray[80]
      },
      successIcon: {
        color: theme.palette.twilio.blue[60]
      },
      errorIcon: {
        color: theme.palette.status.error[200]
      }
    };
  }
});